import { FunctionComponent, useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { useTranslation } from "react-i18next";

import { Checkbox } from "@/components/Checkbox/Checkbox";
import { Spinner } from "@/components/Spinner/Spinner";
import { classNames } from "@/utils/classNames";

import { ReactComponent as ChevronDown } from "@assets/icons/inbox/chevron-down.svg";
import { ReactComponent as ChevronUp } from "@assets/icons/inbox/chevron-up.svg";

import { SvgIcon } from "@/components/Icon/SvgIcon";

import { useViewportClampedPanel } from "@/utils/useViewportClampedPanel";

import { getComposedEventTarget } from "@/utils/getComposedEventTarget";

import { ReengageOptionItem } from "../schema/reengageSource";

interface ReengageMultiSelectProps {
	placeholder: string;
	options: ReengageOptionItem[];
	value: ReengageOptionItem[];
	onChange: (items: ReengageOptionItem[]) => void;
	onSearch?: (term: string) => void;
	searchPlaceholder?: string;
	loading?: boolean;
	testId?: string;
}

/**
 * Multi-select dropdown tailored to the re-engagement form: an empty selection renders as the
 * "All …" placeholder, any number of items can be (de)selected (including back to zero), and the
 * menu is portalled to `document.body` so it is never clipped by the form card.
 */
export const ReengageMultiSelect: FunctionComponent<ReengageMultiSelectProps> = ({
	placeholder,
	options,
	value,
	onChange,
	onSearch,
	searchPlaceholder,
	loading,
	testId,
}) => {
	const { t } = useTranslation();
	const [open, setOpen] = useState(false);
	const [search, setSearch] = useState("");
	const triggerRef = useRef<HTMLDivElement>(null);
	const menuRef = useRef<HTMLDivElement>(null);

	// Keeps the portalled panel inside the viewport wherever the trigger sits (PREC-4138).
	useViewportClampedPanel(menuRef, open);
	const [position, setPosition] = useState<{ top: number; left: number; width: number } | null>(
		null
	);

	useEffect(() => {
		const handleClick = (event: MouseEvent) => {
			const target = getComposedEventTarget(event);

			const inTrigger = triggerRef.current?.contains(target as Node);
			const inMenu = menuRef.current?.contains(target as Node);

			if (!inTrigger && !inMenu && open) {
				setOpen(false);
			}
		};

		window.addEventListener("click", handleClick);

		return () => window.removeEventListener("click", handleClick);
	}, [open]);

	useEffect(() => {
		if (!open) {
			return;
		}

		const updatePosition = () => {
			if (triggerRef.current) {
				const rect = triggerRef.current.getBoundingClientRect();

				setPosition({ top: rect.bottom, left: rect.left, width: rect.width });
			}
		};

		updatePosition();
		window.addEventListener("scroll", updatePosition, true);
		window.addEventListener("resize", updatePosition);

		return () => {
			window.removeEventListener("scroll", updatePosition, true);
			window.removeEventListener("resize", updatePosition);
		};
	}, [open]);

	const isChecked = (id: ReengageOptionItem["id"]) =>
		value.some((v) => String(v.id) === String(id));

	const toggle = (item: ReengageOptionItem) => {
		if (isChecked(item.id)) {
			onChange(value.filter((v) => String(v.id) !== String(item.id)));
		} else {
			onChange([...value, item]);
		}
	};

	const handleSearch = (term: string) => {
		setSearch(term);
		onSearch?.(term);
	};

	const triggerLabel = value.length === 0 ? placeholder : value.map((v) => v.title).join(", ");

	return (
		<div ref={triggerRef} className="relative w-full">
			<button
				className="input-styles flex flex-row items-center pr-8 py-2 pl-3 shadow-sm w-full peer"
				data-test={testId}
				type="button"
				onClick={() => setOpen((prev) => !prev)}
			>
				<span
					className={classNames(
						"flex-shrink-0 whitespace-nowrap max-w-full overflow-hidden text-ellipsis text-left",
						value.length === 0 ? "text-text-disabled" : "text-text"
					)}
				>
					{triggerLabel}
				</span>
				<span className="absolute right-[10px] top-1/2 -translate-y-1/2 text-gray-500">
					<SvgIcon
						className="w-[20px] h-[20px] pointer-events-none"
						svgIcon={open ? ChevronUp : ChevronDown}
					/>
				</span>
			</button>

			{open &&
				position &&
				ReactDOM.createPortal(
					<div
						ref={menuRef}
						className="z-[2000] bg-white border border-gray-200 rounded-md max-h-[280px] overflow-y-auto bb-scrollbar-darker shadow-lg"
						style={{
							position: "fixed",
							top: position.top + 4,
							left: position.left,
							width: position.width,
						}}
					>
						{onSearch && (
							<div className="sticky top-0 bg-background-light border-b border-b-border p-2">
								<input
									className="w-full bg-transparent text-sm px-1 py-1 outline-none"
									placeholder={searchPlaceholder ?? t("basics.search")}
									value={search}
									onChange={(e) => handleSearch(e.target.value)}
								/>
							</div>
						)}
						{loading ? (
							<div className="flex justify-center py-4">
								<Spinner />
							</div>
						) : options.length === 0 ? (
							<p className="py-2.5 px-3 text-sm italic text-gray-700">{t("basics.noResults")}</p>
						) : (
							options.map((item) => (
								<div
									key={`reengage-opt-${item.id}`}
									className="flex flex-row items-center text-sm text-gray-900 w-full bg-white hover:bg-gray-100 duration-200 cursor-pointer"
								>
									<Checkbox
										className="pl-2"
										isChecked={isChecked(item.id)}
										labelClass="w-full cursor-pointer py-2.5 px-3 pl-0"
										onChange={() => toggle(item)}
									>
										<span className="whitespace-nowrap overflow-hidden text-ellipsis">
											{item.title}
										</span>
									</Checkbox>
								</div>
							))
						)}
					</div>,
					document.body
				)}
		</div>
	);
};
